<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import AssignScheduleModal from './assignScheduleModal.vue';

export default {
  components: {
    AssignScheduleModal
  },
  props: {
    staffId: {
      type: String,
      default: null,
    },
    staffList: {
      type: Array,
      default: null,
    },
    scheduleList: {
      type: Array,
      default: null,
    }
  },
  data() {
    return {
      isAssignStaffSchedule: false,
    };
  },
  computed: {
    filteredList() {
      const staff = this.staffList.results.find(staff => staff.staff_id === this.staffId);
      if (!staff) return []; // Handle case where staff is not found

      return staff.schedules.flatMap(staffSchedule =>
        this.scheduleList.filter(schedule => schedule.schedule_id === staffSchedule.schedule_id)
      );
    },
    branchId() {
      const staff = this.staffList?.results?.find(s => s.staff_id === this.staffId);
      return staff?.branch?.branch_id ?? this.$route.params.branchId;
    }
  },
  methods: {
    ...mapActions('appointments', ['assignStaffSchedule', 'deleteStaffSchedule', 'fetchStaffList']),
    async refreshPage() {
      try {
        await this.fetchStaffList(this.branchId);
      } catch (err) {
        console.error(err);
        useAlert(err);
      }
    },
    async handleDeleteStaffSchedule(scheduleId) {
      try {
        await this.deleteStaffSchedule({
          staffId: this.staffId,
          scheduleId
        });
      } catch (err) {
        console.error(err);
        useAlert(err);
      } finally {
        this.refreshPage();
      }
    }
  }
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-md">
    <!-- Assign Schedule Modal -->
    <div 
      v-if="isAssignStaffSchedule" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <AssignScheduleModal
        @close="isAssignStaffSchedule = false" 
        :staffId="staffId" 
        @refresh="refreshPage" 
      />
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-4 flex-wrap gap-2">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ $t('BRANCHES.VIEW_STAFF_SCHEDULE.TITLE') }}</h2>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        size="small"
        @click="isAssignStaffSchedule = true"
      >
        {{ $t('BRANCHES.VIEW_STAFF_SCHEDULE.ASSIGN_SCHEDULES') }}
      </woot-button>
    </div>

    <!-- Schedule List -->
    <div v-if="filteredList.length !== 0">
      <div 
        v-for="schedule in filteredList" 
        :key="schedule.schedule_id"
        class="flex justify-between items-center p-3 border-b last:border-none"
      >
        <div class="flex items-center gap-2">
          <fluent-icon icon="calendar" class="text-gray-600 dark:text-gray-300" />
          <p class="text-md font-medium text-gray-800 dark:text-gray-100 m-0">
            {{ schedule.name }}
          </p>
        </div>
        <button
          @click="handleDeleteStaffSchedule(schedule.schedule_id)" 
          class="px-3 py-1 text-sm font-medium text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition"
        >
          {{ $t('BRANCHES.VIEW_STAFF_SCHEDULE.DELETE') }}
        </button>
      </div>
    </div>
    <!-- Empty State -->
    <div v-else class="text-center text-gray-500 dark:text-gray-400">
      <p>{{ $t('BRANCHES.VIEW_STAFF_SCHEDULE.NO_SCHEDULES') }}</p>
    </div>
  </div>
</template>
